// src/components/layout/navbar/config.ts
import { CATEGORIES, getUnifiedPath } from "@/constants/sectors/onChainPage";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type FlyoutType =
  | 'charts'
  | 'sectors'
  | 'macro';

export interface NavItem {
  label: string;
  path: string;
  description?: string;
  children?: NavItem[];
  /** Type of flyout panel to show on hover (desktop) */
  flyoutType?: FlyoutType;
  /** Extended description shown in flyout */
  flyoutDescription?: string;
  /** Feature highlights shown as bullet points in flyout */
  flyoutFeatures?: string[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  /** If true, this is a section header label (e.g., "RESEARCH") not a navigable group */
  isSection?: boolean;
}

export interface MultiLevelNavItem {
  label: string;
  path?: string | undefined;
  description?: string | undefined;
  children?: MultiLevelNavItem[] | undefined;
}

export interface MultiLevelDropdownProps {
  items: MultiLevelNavItem[];
  onNavigate: (path: string) => void;
  level?: number;
  parentRect?: DOMRect | null;
}

export interface MobileAccordionItemProps {
  item: MultiLevelNavItem;
  onNavigate: (path: string) => void;
  level?: number;
}

// ============================================================================
// NAVIGATION CONFIGURATION
// ============================================================================

/**
 * Main navigation structure
 * 📝 Edit this array to add/remove navigation items
 *
 * Flat top-level structure:
 * - Home
 * - Token Screener (expandable subcategories)
 * - Sectors (All with popup, Compare)
 * - Macro
 * - Indicators
 * - ETFs
 * - Investors
 * - Workspace
 * - Portfolio
 * - AI Assistant
 */
export const navigation: NavGroup[] = [
  // ============================================
  // HOME
  // ============================================
  {
    label: "Home",
    items: [
      {
        label: "Home",
        path: "/app",
        description: "Your research command center",
      },
    ],
  },

  // ============================================
  // TOKENS (expandable subcategories)
  // ============================================
  {
    label: "Tokens",
    items: [
      {
        label: "Screener",
        path: "/app/tokens",
        description: "Screen and analyze crypto tokens",
      },
      {
        label: "Charts",
        path: "/app/tokens/charts",
        description: "Visual token analysis",
        flyoutType: "charts",
        flyoutDescription: "Interactive visualizations for analyzing token metrics and relationships",
        children: [
          { label: "Scatter", path: "/app/tokens/charts/scatter", description: "XY scatter plots" },
          { label: "Heatmap", path: "/app/tokens/charts/heatmap", description: "Time-series heatmaps" },
          { label: "Ranking", path: "/app/tokens/charts/distribution", description: "Token rankings" },
          { label: "Correlation", path: "/app/tokens/charts/correlation", description: "Correlation matrix" },
          { label: "Treemap", path: "/app/tokens/charts/treemap", description: "Hierarchical treemap" },
          { label: "Parallel", path: "/app/tokens/charts/parallel", description: "Multi-axis comparison" },
        ],
      },
      {
        label: "Movers",
        path: "/app/tokens/movers",
        description: "Top gainers and losers",
      },
      {
        label: "Compare",
        path: "/app/tokens/compare",
        description: "Side-by-side token comparison",
      },
      {
        label: "Watchlists",
        path: "/app/tokens/watchlists",
        description: "Your saved token lists",
      },
      {
        label: "Alerts",
        path: "/app/tokens/alerts",
        description: "Price and volume alerts",
      },
    ],
  },

  // ============================================
  // SECTORS (All with popup, Compare)
  // ============================================
  {
    label: "Sectors",
    items: [
      {
        label: "All Sectors",
        path: "/app/sectors",
        description: "Crypto sector analysis",
        flyoutType: "sectors",
      },
      {
        label: "Compare",
        path: "/app/sectors/compare",
        description: "Compare sectors side-by-side",
      },
    ],
  },

  // ============================================
  // MACRO (expandable subcategories)
  // ============================================
  {
    label: "Macro",
    items: [
      {
        label: "Overview",
        path: "/app/macro",
        description: "Global economic data and commodities",
      },
      {
        label: "Economy",
        path: "/app/macro/major-indicators",
        description: "Major economic indicators and calendar",
      },
      {
        label: "Charts",
        path: "/app/macro/economy-charts",
        description: "Comprehensive collection of macro charts",
      },
      {
        label: "Fed Rates",
        path: "/app/macro/fed-probabilities",
        description: "Rate decision forecasts",
      },
      {
        label: "Correlations",
        path: "/app/macro/correlations",
        description: "Cross-asset relationships",
      },
      {
        label: "FX",
        path: "/app/macro/fx",
        description: "Currency pairs & DXY",
      },
      {
        label: "Commodities",
        path: "/app/macro/commodities",
        description: "Gold, oil, and more",
      },
    ],
  },

  // ============================================
  // INDICES
  // ============================================
  {
    label: "Indices",
    items: [
      {
        label: "Indices",
        path: "/app/indicators",
        description: "Sentiment and on-chain metrics",
      },
    ],
  },

  // ============================================
  // ETFs
  // ============================================
  {
    label: "ETFs",
    items: [
      {
        label: "ETFs",
        path: "/app/etfs",
        description: "Crypto ETF flows and analytics",
      },
    ],
  },

  // ============================================
  // INVESTORS
  // ============================================
  {
    label: "Investors",
    items: [
      {
        label: "Investors",
        path: "/app/investors",
        description: "Institutional activity tracking",
      },
    ],
  },

  // ============================================
  // SOCIAL SENTIMENT & NEWS
  // ============================================
  {
    label: "Social Sentiment & News",
    items: [
      {
        label: "Twitter",
        path: "/app/social/twitter",
        description: "AI-analyzed crypto Twitter",
      },
      {
        label: "YouTube",
        path: "/app/social/youtube",
        description: "AI-extracted video insights",
      },
      {
        label: "News",
        path: "/app/social/news",
        description: "Aggregated crypto news",
      },
    ],
  },

  // ============================================
  // WORKSPACE
  // ============================================
  {
    label: "Workspace",
    items: [
      {
        label: "Dashboards",
        path: "/app/workspace/dashboards",
        description: "Your personalized custom dashboards",
      },
      {
        label: "Sheets",
        path: "/app/workspace/sheets",
        description: "Spreadsheets with live crypto data and formulas",
      },
    ],
  },

  // ============================================
  // PORTFOLIO
  // ============================================
  {
    label: "Portfolio",
    items: [
      {
        label: "Overview",
        path: "/app/portfolio/overview",
        description: "Portfolio overview and holdings",
      },
      {
        label: "Metrics",
        path: "/app/portfolio/metrics",
        description: "Portfolio metrics and risk management",
      },
      {
        label: "Alerts",
        path: "/app/portfolio/alerts",
        description: "Portfolio alerts and notifications",
      },
    ],
  },

  // ============================================
  // AI ASSISTANT
  // ============================================
  {
    label: "AI Assistant",
    items: [
      {
        label: "Chat",
        path: "/app/assistant",
        description: "AI Assistant - full-screen chat and tools",
      },
    ],
  },
];

// ============================================================================
// ONCHAIN NAVIGATION BUILDER
// ============================================================================

/**
 * Builds the OnChain navigation structure from CATEGORIES
 * This creates the multi-level dropdown for OnChain section
 */
export const buildOnChainNavigation = (): MultiLevelNavItem[] => {
  const orderKeys = [
    "overview",
    "bitcoin",
    "cex-tokens",
    "bitcoin-ecosystem",
    "core-infrastructure",
    "defi",
    "stablecoins",
    "assets",
    "infrastructure",
    "emerging",
    "miscellaneous",
  ] as const;

  const items: MultiLevelNavItem[] = [];

  orderKeys.forEach((key) => {
    const cat = CATEGORIES[key];
    if (!cat) return;
    const parent: MultiLevelNavItem = {
      label: cat.title,
      path: cat.route ? `/app/${cat.route}` : undefined,
      description: `${cat.title} protocols and analysis`,
      children: [],
    };

    // Add subcategory tabs as children
    if (cat.tabs && cat.tabs.length > 0) {
      parent.children = cat.tabs.map((t) => ({
        label: t.label,
        path: getUnifiedPath(key, t.id),
        description: t.description,
        children: [],
      }));

      // Add dropdown (third-level) under corresponding tab
      if (cat.dropdowns) {
        Object.entries(cat.dropdowns).forEach(([groupKey, groupTabs]) => {
          const tabNode = parent.children!.find(
            (c) =>
              (c &&
                c.label &&
                c.label.toLowerCase().includes(groupKey.replace(/-/g, ""))) ||
              c?.path?.includes(`/${groupKey}`)
          );
          // Fallback: locate by id via path segment match
          const tabNodeByPath = parent.children!.find((c) =>
            c.path?.includes(`/${groupKey}`)
          );
          const hostNode = tabNode || tabNodeByPath;
          if (hostNode) {
            hostNode.children = groupTabs.map((gt) => ({
              label: gt.label,
              path: getUnifiedPath(key, groupKey, gt.id),
              description: gt.description,
            }));
          }
        });
      }
    }

    items.push(parent);
  });

  return items;
};
